import { useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AutoGrowTextarea } from "@/components/AutoGrowTextarea";

export const toRecipients = (text) => text.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);

const PreviewButton = ({ k, cfg, recipientsText }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/settings/preview", { key: k, email: { ...cfg, recipients: toRecipients(recipientsText) } });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button data-testid={`email-preview-btn-${k}`} type="button" variant="outline" size="sm" className="gap-1.5" onClick={open} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview
      </Button>
      <Dialog open={!!data} onOpenChange={(o) => !o && setData(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle data-testid={`email-preview-subject-${k}`}>{data?.subject}</DialogTitle>
            <DialogDescription>Preview with sample details.</DialogDescription>
          </DialogHeader>
          <iframe title="Email preview" data-testid={`email-preview-frame-${k}`} srcDoc={data?.html} sandbox="" className="h-[60vh] w-full rounded-md border border-border bg-white" />
        </DialogContent>
      </Dialog>
    </>
  );
};

export const EmailSettingCard = ({ k, meta, cfg, recipientsText, onChange, onRecipients }) => (
  <Card className="p-5" data-testid={`email-card-${k}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="font-heading text-lg font-bold">{meta.label}</h3>
        <p className="text-sm text-muted-foreground">{meta.sent_to}</p>
      </div>
      <div className="flex items-center gap-3">
        <PreviewButton k={k} cfg={cfg} recipientsText={recipientsText} />
        <label className="flex items-center gap-2 text-sm font-medium">
          <Switch data-testid={`email-enabled-${k}`} checked={cfg.enabled} onCheckedChange={(v) => onChange({ enabled: v })} />
          {cfg.enabled ? "On" : "Off"}
        </label>
      </div>
    </div>
    {k === "reset" && !cfg.enabled && <p className="mt-3 text-sm text-amber-700 dark:text-amber-300">While off, "Forgot password?" will not send anything.</p>}
    <div className={`mt-4 grid gap-4 ${cfg.enabled ? "" : "opacity-60"}`}>
      {meta.has_recipients && (
        <div className="space-y-2">
          <Label htmlFor={`rcpt-${k}`}>Send to (comma-separated emails)</Label>
          <Input id={`rcpt-${k}`} data-testid={`email-recipients-${k}`} value={recipientsText} onChange={(e) => onRecipients(e.target.value)} placeholder="accounts@mokora.co.uk" className="font-mono text-sm" />
          {(k === "submission" || k === "month_submission") && (
            <label className="flex items-center gap-2 pt-1 text-sm">
              <Switch data-testid="email-notify-approvers" checked={cfg.notify_approvers} onCheckedChange={(v) => onChange({ notify_approvers: v })} />
              Also send to the contractor's assigned approvers
            </label>
          )}
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor={`subj-${k}`}>Subject</Label>
        <Input id={`subj-${k}`} data-testid={`email-subject-${k}`} value={cfg.subject} onChange={(e) => onChange({ subject: e.target.value })} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`body-${k}`}>Message</Label>
        <AutoGrowTextarea id={`body-${k}`} data-testid={`email-body-${k}`} value={cfg.body} onChange={(e) => onChange({ body: e.target.value })} className="min-h-[120px]" />
        <p className="text-xs text-muted-foreground">
          Leave a blank line between paragraphs. You can use: {meta.placeholders.map((p) => <code key={p} className="mr-1.5 rounded bg-secondary px-1 py-0.5 font-mono">{`{${p}}`}</code>)}
        </p>
      </div>
      <div className="space-y-2 sm:max-w-sm">
        <Label htmlFor={`btn-${k}`}>Button text</Label>
        <Input id={`btn-${k}`} data-testid={`email-button-${k}`} value={cfg.button_label} onChange={(e) => onChange({ button_label: e.target.value })} />
      </div>
    </div>
  </Card>
);
